import { PaymentMethodProps } from './PaymentMethod';
import PaymentDetails from './PaymentDetails';
import CloseIcon from '../../../assets/icons/actions/CloseIcon';
import Theme from '../../Theme';
import React, { useMemo, useState } from 'react';
import PaymentMethodOption from './PaymentMethodOption';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import PaymentManager from '../../../services/api/PaymentManager';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import ConfirmCancelModal from '../../../modals/ConfirmCancelModal';

export default function AttachablePaymentMethod({ children, ...props }: PaymentMethodProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentMethods:AttachablePaymentMethod' });

  const { id } = props;

  const { paymentOptions, findSavedPaymentMethod } = usePaymentFlow();

  const savedInfo = useMemo(() => findSavedPaymentMethod(id), [paymentOptions]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const deletePaymentMethod = async () => {
    setDeleting(true);
    const { success } = await PaymentManager.removePaymentOption(savedInfo.id);
    setDeleting(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_delete_payment_method'));
    }

    toast.success(t('toasts.payment_method_deleted'));

    return setShowDeleteModal(false);
  };

  return (
    <PaymentMethodOption
      {...props}
      extra={
        <>
          {Boolean(savedInfo) && (
            <span className="flex items-center min-w-0 gap-xs">
              <PaymentDetails hideIcons={!savedInfo.payment_method_brand} {...savedInfo} />
              <div
                className="border border-gray-300 rounded-full flex justify-center items-center min-w-5 h-5 cursor-pointer"
                onClick={onDeleteClick}
              >
                <CloseIcon className="h-2 w-2" color={Theme.color.gray['300']} />
              </div>
            </span>
          )}
          {!savedInfo && props.extra}
        </>
      }
    >
      {children}
      <ConfirmCancelModal
        openModal={showDeleteModal}
        loading={deleting}
        title={t('modals.delete_payment_method.title')}
        primaryButtonTitle={t('buttons.confirm')}
        secondaryButtonTitle={t('buttons.cancel')}
        onConfirm={() => deletePaymentMethod()}
        onCancel={() => setShowDeleteModal(false)}
      />
    </PaymentMethodOption>
  );
}
